<template>
  <div class="turnstile-wrapper">
    <!-- Dev mode: auto-bypass -->
    <div v-if="isDevMode" class="turnstile-dev-bypass">
      <i class="zmdi zmdi-check-circle" />
      <span>Turnstile bypassed (dev mode)</span>
    </div>
    <!-- Production: real widget -->
    <template v-else>
      <div :id="containerId" class="turnstile-container" />
      <p v-if="error" class="turnstile-error">{{ error }}</p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  loadTurnstile,
  renderTurnstileWidget,
  resetTurnstileWidget,
  getTurnstileResponse,
  removeTurnstileWidget,
  executeTurnstileWidget,
} from '@/shared/utils/turnstile'

const DEV_CAPTCHA_TOKEN = 'DEV_CAPTCHA_BYPASS_TOKEN'
const isDevMode = import.meta.env.DEV

const props = defineProps<{
  containerId?: string
}>()

const { t } = useI18n()

const emit = defineEmits<{
  (e: 'verified', token: string): void
  (e: 'expired'): void
  (e: 'error'): void
}>()

const containerId = props.containerId || `turnstile-${Math.random().toString(36).slice(2, 9)}`
const widgetId = ref<string | null>(null)
const error = ref('')
const token = ref('')

onMounted(async () => {
  // Dev mode: auto-emit verified with mock token
  if (isDevMode) {
    token.value = DEV_CAPTCHA_TOKEN
    emit('verified', DEV_CAPTCHA_TOKEN)
    console.log('[GonkaGame] Turnstile bypassed in dev mode')
    return
  }

  try {
    error.value = ''
    await loadTurnstile()
    
    widgetId.value = await renderTurnstileWidget(containerId, {
      onSuccess: (newToken) => {
        token.value = newToken
        emit('verified', newToken)
      },
      onError: () => {
        error.value = t('auth.captchaTurnstileError')
        emit('error')
      },
      onExpired: () => {
        token.value = ''
        emit('expired')
      },
    })
  } catch (err) {
    error.value = t('auth.captchaLoadError')
    console.error('[GonkaGame] Turnstile load error:', err)
  }
})

onBeforeUnmount(() => {
  if (widgetId.value !== null) {
    removeTurnstileWidget(widgetId.value)
  }
})

/**
 * Reset the widget for re-verification
 */
const reset = () => {
  if (widgetId.value !== null) {
    resetTurnstileWidget(widgetId.value)
    token.value = ''
  }
}

/**
 * Get current token (for external use)
 */
const getToken = (): string => {
  if (widgetId.value !== null) {
    return getTurnstileResponse(widgetId.value)
  }
  return ''
}

/**
 * Trigger manual verification (for execution: 'execute')
 */
const showCaptcha = () => {
  if (widgetId.value !== null) {
    executeTurnstileWidget(widgetId.value)
  }
}

// Expose methods for parent components
defineExpose({
  reset,
  getToken,
  showCaptcha,
})
</script>

<style scoped>
.turnstile-wrapper {
  margin: 16px 0;
}

.turnstile-container {
  display: flex;
  justify-content: center;
}

.turnstile-dev-bypass {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--ds-warning-soft);
  border: 1px dashed var(--ds-warning-soft);
  border-radius: var(--ds-radius-control);
  color: var(--hm-warning);
  font-size: var(--p-text-13);
  font-weight: var(--ds-weight-medium);
}

.turnstile-dev-bypass i {
  font-size: var(--p-text-18);
}

.turnstile-error {
  color: var(--hm-danger);
  font-size: var(--ds-text-size-body);
  margin-top: 8px;
  text-align: center;
}
</style>
